// Curated list of coordinate reference systems commonly used for point-cloud /
// survey data. This is intentionally NOT the full EPSG registry (6000+ entries)
// — just the systems likely to appear, so the project dropdown stays usable.
// Stored as informational metadata on the project (see the EPSG field in
// project create/edit).

export interface EpsgOption {
  code: number;
  label: string;
}

const NAMED: EpsgOption[] = [
  { code: 27700, label: 'British National Grid (OSGB36) — EPSG:27700' },
  { code: 2157,  label: 'Ireland — Irish Transverse Mercator (ITM) — EPSG:2157' },
  { code: 29903, label: 'Ireland — Irish Grid (TM75) — EPSG:29903' },
  { code: 3908,  label: 'Bosnia and Herzegovina, Montenegro — MGI 1901 / Balkans zone 6 — EPSG:3908' },
  { code: 3907,  label: 'Croatia (west) — MGI 1901 / Balkans zone 5 — EPSG:3907' },
  { code: 3909,  label: 'Serbia, Kosovo — MGI 1901 / Balkans zone 7 — EPSG:3909' },
  { code: 3910,  label: 'Serbia (east) — MGI 1901 / Balkans zone 8 — EPSG:3910' },
  { code: 3765,  label: 'Croatia — HTRS96 / Croatia TM — EPSG:3765' },
  { code: 3794,  label: 'Slovenia — D96 / Slovene National Grid — EPSG:3794' },
  { code: 3912,  label: 'Slovenia — D48 / Slovene National Grid (old) — EPSG:3912' },
  { code: 8682,  label: 'Serbia — SRB_ETRS89 / UTM 34N — EPSG:8682' },
  { code: 6204,  label: 'North Macedonia — Macedonia State Coordinate System — EPSG:6204' },
  { code: 6962,  label: 'Albania — ETRS89 / Albania LCC 2010 — EPSG:6962' },
  { code: 2100,  label: 'Greece — GGRS87 / Greek Grid — EPSG:2100' },
  { code: 7801,  label: 'Bulgaria — BGS2005 / CCS2005 — EPSG:7801' },
  { code: 3844,  label: 'Romania — Stereo 70 — EPSG:3844' },
  { code: 23700, label: 'Hungary — HD72 / EOV — EPSG:23700' },
  { code: 31254, label: 'Austria — MGI / GK West — EPSG:31254' },
  { code: 31255, label: 'Austria — MGI / GK Central — EPSG:31255' },
  { code: 31256, label: 'Austria — MGI / GK East — EPSG:31256' },
  { code: 31287, label: 'Austria — MGI / Austria Lambert — EPSG:31287' },
  { code: 5514,  label: 'Czechia, Slovakia — S-JTSK / Krovak East North — EPSG:5514' },
  { code: 2180,  label: 'Poland — CS92 — EPSG:2180' },
  { code: 2176,  label: 'Poland — CS2000 zone 5 — EPSG:2176' },
  { code: 2177,  label: 'Poland — CS2000 zone 6 — EPSG:2177' },
  { code: 2178,  label: 'Poland — CS2000 zone 7 — EPSG:2178' },
  { code: 2179,  label: 'Poland — CS2000 zone 8 — EPSG:2179' },
  { code: 31466, label: 'Germany — DHDN / Gauss-Krüger zone 2 — EPSG:31466' },
  { code: 31467, label: 'Germany — DHDN / Gauss-Krüger zone 3 — EPSG:31467' },
  { code: 31468, label: 'Germany — DHDN / Gauss-Krüger zone 4 — EPSG:31468' },
  { code: 31469, label: 'Germany — DHDN / Gauss-Krüger zone 5 — EPSG:31469' },
  { code: 4647,  label: 'Germany — ETRS89 / UTM 32N with zone prefix — EPSG:4647' },
  { code: 5650,  label: 'Germany — ETRS89 / UTM 33N with zone prefix — EPSG:5650' },
  { code: 2056,  label: 'Switzerland — CH1903+ / LV95 — EPSG:2056' },
  { code: 21781, label: 'Switzerland — CH1903 / LV03 — EPSG:21781' },
  { code: 2154,  label: 'France — RGF93 / Lambert-93 — EPSG:2154' },
  { code: 3942,  label: 'France — RGF93 / CC42 — EPSG:3942' },
  { code: 3943,  label: 'France — RGF93 / CC43 — EPSG:3943' },
  { code: 3944,  label: 'France — RGF93 / CC44 — EPSG:3944' },
  { code: 3945,  label: 'France — RGF93 / CC45 — EPSG:3945' },
  { code: 3946,  label: 'France — RGF93 / CC46 — EPSG:3946' },
  { code: 3947,  label: 'France — RGF93 / CC47 — EPSG:3947' },
  { code: 3948,  label: 'France — RGF93 / CC48 — EPSG:3948' },
  { code: 3949,  label: 'France — RGF93 / CC49 — EPSG:3949' },
  { code: 3950,  label: 'France — RGF93 / CC50 — EPSG:3950' },
  { code: 28992, label: 'Netherlands — Amersfoort / RD New — EPSG:28992' },
  { code: 31370, label: 'Belgium — Lambert 72 — EPSG:31370' },
  { code: 3812,  label: 'Belgium — Lambert 2008 — EPSG:3812' },
  { code: 2169,  label: 'Luxembourg — LUREF / Luxembourg TM — EPSG:2169' },
  { code: 25830, label: 'Spain — ETRS89 / UTM 30N — EPSG:25830' },
  { code: 25831, label: 'Spain / Andorra — ETRS89 / UTM 31N — EPSG:25831' },
  { code: 3763,  label: 'Portugal — ETRS89 / PT-TM06 — EPSG:3763' },
  { code: 3003,  label: 'Italy — Monte Mario / Italy zone 1 — EPSG:3003' },
  { code: 3004,  label: 'Italy — Monte Mario / Italy zone 2 — EPSG:3004' },
  { code: 7791,  label: 'Italy — RDN2008 / UTM 32N — EPSG:7791' },
  { code: 7792,  label: 'Italy — RDN2008 / UTM 33N — EPSG:7792' },
  { code: 25832, label: 'Central Europe — ETRS89 / UTM 32N — EPSG:25832' },
  { code: 25833, label: 'Central Europe — ETRS89 / UTM 33N — EPSG:25833' },
  { code: 3006,  label: 'Sweden — SWEREF99 TM — EPSG:3006' },
  { code: 3067,  label: 'Finland — ETRS-TM35FIN — EPSG:3067' },
  { code: 3301,  label: 'Estonia — L-EST97 — EPSG:3301' },
  { code: 3059,  label: 'Latvia — LKS-92 / Latvia TM — EPSG:3059' },
  { code: 3346,  label: 'Lithuania — LKS94 / Lithuania TM — EPSG:3346' },
  { code: 2193,  label: 'New Zealand — NZGD2000 / NZTM — EPSG:2193' },
];

function utmOptions(): EpsgOption[] {
  const out: EpsgOption[] = [];
  for (let z = 1; z <= 60; z++) {
    out.push({ code: 32600 + z, label: `WGS84 / UTM zone ${z}N — EPSG:${32600 + z}` });
  }
  for (let z = 1; z <= 60; z++) {
    out.push({ code: 32700 + z, label: `WGS84 / UTM zone ${z}S — EPSG:${32700 + z}` });
  }
  for (let z = 28; z <= 38; z++) {
    if (!NAMED.some((o) => o.code === 25800 + z)) {
      out.push({ code: 25800 + z, label: `ETRS89 / UTM zone ${z}N — EPSG:${25800 + z}` });
    }
  }
  for (let z = 1; z <= 23; z++) {
    out.push({ code: 26900 + z, label: `NAD83 / UTM zone ${z}N — EPSG:${26900 + z}` });
  }
  return out;
}

export const EPSG_OPTIONS: EpsgOption[] = [...NAMED, ...utmOptions()];

export function epsgLabel(code?: number | null): string {
  if (!code) return '';
  const found = EPSG_OPTIONS.find((o) => o.code === code);
  return found ? found.label : `EPSG:${code}`;
}
